import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/customSupabaseClient';

export function useLiveStudio() {
  const [liveSessions, setLiveSessions] = useState([]);
  const [recentNotes, setRecentNotes] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [loadingNotes, setLoadingNotes] = useState(true);
  const [error, setError] = useState(null);
  const channelsRef = useRef([]);

  const fetchLiveSessions = useCallback(async () => {
    try {
      const { data, error } = await supabase.rpc('admin_get_live_sessions');
      if (error) throw error;
      setLiveSessions(data || []);
    } catch (err) {
      console.error('Error fetching live sessions:', err);
      setError('Failed to load live sessions.');
    } finally {
      setLoadingSessions(false);
    }
  }, []);

  const fetchRecentNotes = useCallback(async (exerciseId = null) => {
    try {
      const params = { p_limit: null, p_exercise_id: exerciseId || null, p_user_id: null };
      const [notesRes, flagsRes] = await Promise.all([
        supabase.rpc('admin_get_recent_notes', params),
        supabase.rpc('staff_get_open_note_flags'),
      ]);
      if (notesRes.error) throw notesRes.error;
      if (flagsRes.error) console.error('Error fetching note flags:', flagsRes.error);
      const flagsByCompletion = new Map(
        (flagsRes.data || []).map((f) => [f.exercise_completion_id, f])
      );
      setRecentNotes((notesRes.data || []).map((n) => ({
        ...n,
        flag: flagsByCompletion.get(n.completion_id) || null,
      })));
    } catch (err) {
      console.error('Error fetching recent notes:', err);
      setError('Failed to load notes.');
    } finally {
      setLoadingNotes(false);
    }
  }, []);

  const replyToNote = useCallback(async (exerciseCompletionId, replyText) => {
    const { data, error } = await supabase.rpc('admin_reply_to_note', {
      p_exercise_completion_id: exerciseCompletionId,
      p_reply_text: replyText,
    });
    if (error) throw error;
    await fetchRecentNotes();
    return data;
  }, [fetchRecentNotes]);

  const setNoteTriage = useCallback(async (exerciseCompletionId, status) => {
    const { data, error } = await supabase.rpc('admin_set_note_triage', {
      p_exercise_completion_id: exerciseCompletionId,
      p_status: status,
    });
    if (error) throw error;
    await fetchRecentNotes();
    return data;
  }, [fetchRecentNotes]);

  const flagNote = useCallback(async (exerciseCompletionId, reason, comment) => {
    const { data, error } = await supabase.rpc('staff_flag_note', {
      p_exercise_completion_id: exerciseCompletionId,
      p_reason: reason,
      p_comment: comment || null,
    });
    if (error) throw error;
    await fetchRecentNotes();
    return data;
  }, [fetchRecentNotes]);

  const resolveNoteFlag = useCallback(async (flagId) => {
    const { error } = await supabase.rpc('staff_resolve_note_flag', { p_flag_id: flagId });
    if (error) throw error;
    await fetchRecentNotes();
  }, [fetchRecentNotes]);

  useEffect(() => {
    fetchLiveSessions();
    fetchRecentNotes();

    const sessionsChannel = supabase
      .channel('live_studio_sessions')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'user_training_sessions',
      }, () => {
        fetchLiveSessions();
      })
      .subscribe();

    const notesChannel = supabase
      .channel('live_studio_notes')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'user_exercise_completion',
      }, () => {
        fetchRecentNotes();
        fetchLiveSessions();
      })
      .subscribe();

    const repliesChannel = supabase
      .channel('live_studio_replies')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'exercise_note_replies',
      }, () => {
        fetchRecentNotes();
      })
      .subscribe();

    // Bulk triage updates arrive as one event per row; refetch once per burst.
    let triageRefetchTimer = null;
    const triageChannel = supabase
      .channel('live_studio_triage')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'exercise_note_triage',
      }, () => {
        clearTimeout(triageRefetchTimer);
        triageRefetchTimer = setTimeout(fetchRecentNotes, 500);
      })
      .subscribe();

    let flagsRefetchTimer = null;
    const flagsChannel = supabase
      .channel('live_studio_note_flags')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'note_flags',
      }, () => {
        clearTimeout(flagsRefetchTimer);
        flagsRefetchTimer = setTimeout(fetchRecentNotes, 500);
      })
      .subscribe();

    channelsRef.current = [sessionsChannel, notesChannel, repliesChannel, triageChannel, flagsChannel];

    return () => {
      clearTimeout(triageRefetchTimer);
      clearTimeout(flagsRefetchTimer);
      channelsRef.current.forEach(ch => supabase.removeChannel(ch));
    };
  }, [fetchLiveSessions, fetchRecentNotes]);

  return {
    liveSessions,
    recentNotes,
    loadingSessions,
    loadingNotes,
    error,
    replyToNote,
    setNoteTriage,
    flagNote,
    resolveNoteFlag,
    refreshSessions: fetchLiveSessions,
    refreshNotes: fetchRecentNotes,
  };
}
